import { HttpErrorResponse } from "@angular/common/http";
import { Component, inject, OnDestroy, OnInit } from "@angular/core";
import { ActivatedRoute, RouterLink } from "@angular/router";
import { ButtonModule } from "primeng/button";
import { CardModule } from "primeng/card";
import { DialogService, DynamicDialogRef } from "primeng/dynamicdialog";
import { MessagesModule } from "primeng/messages";
import { ProgressSpinnerModule } from "primeng/progressspinner";
import { TooltipModule } from "primeng/tooltip";
import { Subject, takeUntil } from "rxjs";
import { AccountService } from "src/app/common/services/account.service";
import { FileContentService } from "src/app/common/services/file-content.service";
import { NotificationService } from "src/app/common/services/notification.service";
import { refreshInterval } from "src/app/config";
import { DocumentsSpace } from "src/app/modules/documents-space/documents-space.model";
import { DocumentsSpaceService } from "src/app/modules/documents-space/documents-space.service";
import { DocumentsSpaceChatComponent } from "src/app/modules/documents-space/documents-space-chat/documents-space-chat.component";
import { DocumentsSpaceChatReadonlyComponent } from "src/app/modules/documents-space/documents-space-chat-readonly/documents-space-chat-readonly.component";
import { DocumentsSpaceSettingsComponent } from "src/app/modules/documents-space/documents-space-settings/documents-space-settings.component";

@Component({
  selector: "app-documents-space-details",
  standalone: true,
  imports: [
    ButtonModule,
    TooltipModule,
    CardModule,
    RouterLink,
    MessagesModule,
    ProgressSpinnerModule,
    DocumentsSpaceChatComponent,
  ],
  templateUrl: "./documents-space-details.component.html",
  styleUrl: "./documents-space-details.component.scss",
})
export class DocumentsSpaceDetailsComponent implements OnInit, OnDestroy {
  private readonly destroySubject = new Subject<void>();
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly notificationService = inject(NotificationService);
  private readonly documentsSpaceService = inject(DocumentsSpaceService);
  private readonly dialogService = inject(DialogService);
  private readonly fileContentService = inject(FileContentService);
  protected readonly accountService = inject(AccountService);
  private timeout?: NodeJS.Timeout;

  documentsSpaceSettingsRef?: DynamicDialogRef;
  ownerHistoryRef?: DynamicDialogRef;

  protected DocumentsSpace!: DocumentsSpace;
  documentsSpace: DocumentsSpace | undefined;
  shareLink = window.location.href;

  audioId!: string;
  async ngOnInit(): Promise<void> {
    const documentsSpaceId = this.activatedRoute.snapshot.paramMap.get("id");
    if (documentsSpaceId === null) {
      this.notificationService.showError("Documents space not found", "Documents space not found");
      return;
    }
    try {
      await this.loadItem(documentsSpaceId);
    } catch (error) {
      let message = "An error occurred while fetching details";
      if (error instanceof HttpErrorResponse) {
        if (error.error.detail) {
          message += `: ${error.error.detail}`;
        }
      }
      this.notificationService.showWarning(message);
    }
  }

  async loadItem(documentsSpaceId: string): Promise<void> {
    clearTimeout(this.timeout);
    this.documentsSpace = await this.documentsSpaceService.get(documentsSpaceId);
    if (this.documentsSpace.status === "UPLOADING") {
      this.timeout = setTimeout(() => void this.loadItem(documentsSpaceId), refreshInterval);
    }
  }

  ngOnDestroy(): void {
    this.destroySubject.next();
    this.destroySubject.complete();
    this.ownerHistoryRef?.close();
    clearTimeout(this.timeout);
  }
  async openOwnerHistory(): Promise<void> {
    if (!this.documentsSpace) {
      return;
    }
    if (this.ownerHistoryRef) {
      this.ownerHistoryRef.close();
    }
    const messages = await this.documentsSpaceService.getOwnerMessages(this.documentsSpace.id);

    this.ownerHistoryRef = this.dialogService.open(DocumentsSpaceChatReadonlyComponent, {
      header: $localize`:@@documents_space_details_owner_history:Historique du propriétaire`,

      draggable: true,
      width: "40%",
      height: "400px",
      modal: false,
      resizable: true,
      data: {
        messages,
      },
    });
  }

  openSharedSpaceSetting(): void {
    this.documentsSpaceSettingsRef = this.dialogService.open(DocumentsSpaceSettingsComponent, {
      header: $localize`:@@documents_space_settings_edit:Modifier l'espace`,
      width: "70%",
      data: {
        documentsSpace: this.documentsSpace,
      },
    });
    this.documentsSpaceSettingsRef.onClose.pipe(takeUntil(this.destroySubject)).subscribe((data: DocumentsSpace) => {
      if (data) {
        this.documentsSpace = data;
        this.timeout = setTimeout(() => void this.loadItem(this.documentsSpace!.id), refreshInterval);
      }
    });
  }

  async downloadFile(filePath: string, filename: string): Promise<void> {
    await this.fileContentService.download(filePath, filename);
  }
}
